import './Skills.scss';
import { primalKnowledgeProfile } from '../../../shared/rules/primalKnowledge.mjs';
import { thaumaturgeSkillBonus } from '../../../shared/rules/divineOrder';
import { magicianSkillBonus } from '../../../shared/rules/primalOrder';
import { withBeastFormSkill } from '../../../shared/rules/beastForm';
import { jackOfAllTradesBonus } from '../../../shared/rules/classExpertise';
import { getBonusProficiencies } from '../../../shared/rules/subclasses/bard/lore.mjs';
import {publishTacticalMindCheck} from '../utils/tacticalMindChecks';
import { resolveAbilityCheckDie, getProficientTools } from '../../../shared/rules/abilityChecks.mjs';
import { resolveAbilityCheckRollMode } from '../../../shared/rules/abilityCheckRollMode.mjs';
import { checkMinimumD20, getBuffSkillDiceModifiers } from '../../../shared/rules/effects/buffModifiers.mjs';
import { dieModifiers, modifierDiceGroups, modifierDiceTotal, readModifierDice, modifierDiceBreakdown, modifierDiceRecords } from '../../../shared/rules/effects/rollDiceModifiers.mjs';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { Modal, Card, Button, Form, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { SKILLS } from '../skillSchema';
import proficiencyBonus from '../../../utils/proficiencyBonus';
import { normalizeEquipmentMap } from './equipmentNormalization';
import DockControls from '../components/DockControls';
import {
  rollDiceWithBox,
  setDiceBoxThemeColor,
} from '../../../utils/diceBoxManager';
import {
  DEFAULT_DICE_COLOR,
  normalizeDiceColor,
  applyDiceFaceColor,
} from '../../../utils/diceColors';
import { canUsePrimalKnowledgeForSkill } from '../utils/barbarian';
import { emitCriticalRollEvent } from '../../../utils/criticalRolls';
import { withIndomitableMight } from '../../../shared/indomitableMight';
import { withHalflingLuck } from '../utils/halflingLuck';

const EMPTY_OBJECT = Object.freeze({});

const ABILITY_LABELS = {
  str: 'Strength',
  dex: 'Dexterity',
  con: 'Constitution',
  int: 'Intelligence',
  wis: 'Wisdom',
  cha: 'Charisma',
};

const ABILITY_ICONS = {
  str: '✦', dex: '↟', con: '⬡', int: '✧', wis: '◉', cha: '❖',
};

const ABILITY_ORDER = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

const formatBonus = (value) => `${Number(value) >= 0 ? '+' : ''}${Number(value)}`;

const formatAdjustmentSegment = (value, label) => {
  if (!value) return null;
  const sign = value >= 0 ? '+' : '-';
  return `${sign} ${Math.abs(value)} ${label}`;
};

const normalizeD20Value = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed);
  if (rounded < 1) return 1;
  if (rounded > 20) return 20;
  return rounded;
};

const waitForNextAnimationFrame = () =>
  new Promise((resolve) => {
    if (
      typeof window === 'undefined' ||
      typeof window.requestAnimationFrame !== 'function'
    ) {
      resolve();
      return;
    }

    window.requestAnimationFrame(() => resolve());
  });

export function rollSkill(bonus = 0, d20Override = null) {
  const normalizedOverride = normalizeD20Value(d20Override);
  const d20 =
    normalizedOverride !== null
      ? normalizedOverride
      : Math.floor(Math.random() * 20) + 1;
  const result = d20 + bonus;
  return { result, d20 };
}

// The roll mode lives in shared rules (the server's DM roll requests use it too); re-exported for this file's importers.
export { resolveD20RollMode, resolveAbilityCheckRollMode } from '../../../shared/rules/abilityCheckRollMode.mjs';

const resolveKeptD20 = (values, mode) => {
  if (mode === 'advantage') return Math.max(...values);
  if (mode === 'disadvantage') return Math.min(...values);
  return values[0];
};

// Keep the visual request derived from the same resolved mode that chooses the
// authoritative die.  Callers should never need feature-specific dice logic.
export const buildD20DiceBoxRequest = (mode = 'normal') => [{
  count: mode === 'advantage' || mode === 'disadvantage' ? 2 : 1,
  sides: 20,
}];

/** The breakdown's last step when Indomitable Might raised the total to the Strength score. */
export const indomitableMightNote = (roll) => (roll?.indomitableMight ? `→ ${roll.result} (Indomitable Might: Strength score)` : '');

// What a Heroic Inspiration reroll from the roll log needs later (PlayerTurnActions): the kept and rolled d20s, the
// roll mode, Reliable Talent's inputs, the total before Indomitable Might and Might's inputs.
const heroicRerollRecord = (completed, { keptD20, rolledD20s, rollMode, proficient, rollContext, ability, strengthScore }) => ({
  d20: keptD20, dice: rolledD20s, rollMode, proficient, rollContext, rawTotal: completed.indomitableMight?.rolled ?? completed.result,
  ability, strengthScore,
});

export async function rollSkillWithDiceBox(bonus = 0, options = {}) {
  const { minimumD20, diceColor = null, rollMode = 'normal', rollContext = 'custom-d20', character = null, source = null, diceModifiers = [], proficient = false, ability = null, strengthScore = null } = options || {};
  // Indomitable Might (Barbarian 18): a Strength check or save totals at least the Strength score.
  const mighty = (completed) => {
    const result = withIndomitableMight(character, ability, completed.result, strengthScore);
    return result === completed.result ? completed : { ...completed, result, indomitableMight: { rolled: completed.result } };
  };
  const normalizedRollMode = rollMode === 'advantage' || rollMode === 'disadvantage' ? rollMode : 'normal';
  const normalizedColor = normalizeDiceColor(diceColor) || DEFAULT_DICE_COLOR;

  applyDiceFaceColor(normalizedColor);
  setDiceBoxThemeColor(normalizedColor);
  await waitForNextAnimationFrame();

  try {
    const diceRequest = [...buildD20DiceBoxRequest(normalizedRollMode), ...modifierDiceGroups(diceModifiers)];
    const diceCount = diceRequest[0].count;
    const { rolls } = await rollDiceWithBox(diceRequest);
    const firstGroup = Array.isArray(rolls) ? rolls[0] : undefined;
    const facedD20s = (Array.isArray(firstGroup) ? firstGroup : [firstGroup])
      .map(normalizeD20Value)
      .filter((value) => value !== null)
      .slice(0, diceCount);
    if (facedD20s.length === diceCount) {
      // Halfling Luck rerolls a 1 before anything reads the die.
      const { d20s: rolledD20s, halflingLuck } = await withHalflingLuck(character, facedD20s, { label: source || 'd20' });
      const keptD20 = resolveKeptD20(rolledD20s, normalizedRollMode);
      const modifierDice = readModifierDice({ rolls }, diceModifiers);
      const checkDie = resolveAbilityCheckDie(keptD20, { character, rollContext, proficient, minimumD20 });
      const completed = mighty({ ...rollSkill(Number(bonus) + modifierDiceTotal(diceModifiers, modifierDice), checkDie.effectiveD20), ...checkDie });
      completed.modifierDice = modifierDice;
      completed.diceModifiers = diceModifiers;
      emitCriticalRollEvent({ rawRoll: keptD20, total: completed.result, rollContext, character, source });
      publishTacticalMindCheck({character,context:rollContext,source,total:completed.result,d20s:rolledD20s});
      return {
        ...completed,
        d20: keptD20,
        rolledD20s,
        keptD20,
        rollMode: normalizedRollMode,
        heroicReroll: heroicRerollRecord(completed, { keptD20, rolledD20s, rollMode: normalizedRollMode, proficient, rollContext, ability, strengthScore }),
        usedDiceBox: true,
        ...(halflingLuck ? { halflingLuck } : {}),
      };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Skill roll using dice box failed', error);
  }

  const { d20s: fallbackRolls, halflingLuck } = await withHalflingLuck(character, Array.from(
    { length: normalizedRollMode === 'normal' ? 1 : 2 },
    () => Math.floor(Math.random() * 20) + 1
  ), { label: source || 'd20' });
  const keptD20 = resolveKeptD20(fallbackRolls, normalizedRollMode);
  const modifierDice = dieModifiers(diceModifiers).map(() => Math.floor(Math.random() * 4) + 1);
  const checkDie = resolveAbilityCheckDie(keptD20, { character, rollContext, proficient, minimumD20 });
  const completed = mighty({ ...rollSkill(Number(bonus) + modifierDiceTotal(diceModifiers, modifierDice), checkDie.effectiveD20), ...checkDie });
  completed.modifierDice = modifierDice;
  completed.diceModifiers = diceModifiers;
  emitCriticalRollEvent({ rawRoll: keptD20, total: completed.result, rollContext, character, source });
  publishTacticalMindCheck({character,context:rollContext,source,total:completed.result,d20s:fallbackRolls});
  return {
    ...completed,
    d20: keptD20,
    rolledD20s: fallbackRolls,
    keptD20,
    rollMode: normalizedRollMode,
    heroicReroll: heroicRerollRecord(completed, { keptD20, rolledD20s: fallbackRolls, rollMode: normalizedRollMode, proficient, rollContext, ability, strengthScore }),
    usedDiceBox: false,
    ...(halflingLuck ? { halflingLuck } : {}),
  };
}

export default function Skills({
  form,
  combatState,
  showSkill,
  handleCloseSkill,
  totalLevel,
  strMod,
  dexMod,
  conMod,
  intMod,
  chaMod,
  strScore = null,
  wisMod,
  onSkillsChange,
  onRollResult,
  isDocked = false,
  dockedSide = null,
  onDockClose,
  onDockChange,
}) {
  const params = useParams();
  const safeForm = form ?? {};
  const [primalGrant,setPrimalGrant] = useState(null);
  const [primalSkill,setPrimalSkill] = useState('');
  const [savingPrimal,setSavingPrimal] = useState(false);
  const primalProfile = primalKnowledgeProfile(safeForm);
  const primalChosen = primalGrant?.skill || primalProfile.skill;
  const actorId = String(safeForm._id || params.id || safeForm.characterId || '');
  const liveActor = useRef(actorId);liveActor.current = actorId;
  useEffect(()=>{setPrimalGrant(null);setPrimalSkill('');setSavingPrimal(false);},[actorId]);
  const choosePrimal = async () => {
    if(savingPrimal || !primalSkill)return;
    const requestedActor=actorId;
    setSavingPrimal(true);setError('');
    try {
      const response=await apiFetch(`/characters/${encodeURIComponent(actorId)}/barbarian/primal-knowledge`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({skill:primalSkill})});
      const saved=await response.json();
      if(!response.ok)throw new Error(saved.message || 'Primal Knowledge could not be saved.');
      if(liveActor.current!==requestedActor)return;
      setPrimalGrant(saved);setSkills(saved.skills);onSkillsChange?.(saved.skills,saved);
    }catch(error){if(liveActor.current===requestedActor)setError(error.message);}
    finally{if(liveActor.current===requestedActor)setSavingPrimal(false);}
  };
  const diceFaceColor = useMemo(
    () => normalizeDiceColor(safeForm?.diceColor) || DEFAULT_DICE_COLOR,
    [safeForm?.diceColor],
  );
  const unappliedPrimalGrant = primalGrant && primalProfile.skill !== primalGrant.skill ? primalGrant : null;
  const formSkills = unappliedPrimalGrant?.skills ?? safeForm.skills ?? EMPTY_OBJECT;
  const formProficiencyPoints = unappliedPrimalGrant?.proficiencyPoints ?? safeForm.proficiencyPoints ?? 0;
  const formExpertisePoints = safeForm.expertisePoints || 0;
  const [skills, setSkills] = useState(formSkills);
  const [error, setError] = useState('');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('ability');
  const [collapsedAbilities, setCollapsedAbilities] = useState({});
  const [modifierPrompt, setModifierPrompt] = useState(null);
  const [selectedModifierAbility, setSelectedModifierAbility] = useState('');
  const [isRollingSkill, setIsRollingSkill] = useState(false);
  const [selectedTool, setSelectedTool] = useState('');
  const [toolAbility, setToolAbility] = useState('dex');
  const proficientTools = getProficientTools(safeForm);
  const raceProficiencies = useMemo(() => {
    return new Set(
      Object.entries(form?.race?.skills || {})
        .filter(([, s]) => s?.proficient)
        .map(([key]) => key)
    );
  }, [form?.race?.skills]);
  const backgroundProficiencies = useMemo(() => {
    return new Set(
      Object.entries(form?.background?.skills || {})
        .filter(([, s]) => s?.proficient)
        .map(([key]) => key)
    );
  }, [form?.background?.skills]);
  const raceExpertise = useMemo(() => {
    return new Set(
      Object.entries(form?.race?.skills || {})
        .filter(([, s]) => s?.expertise)
        .map(([key]) => key)
    );
  }, [form?.race?.skills]);
  const backgroundExpertise = useMemo(() => {
    return new Set(
      Object.entries(form?.background?.skills || {})
        .filter(([, s]) => s?.expertise)
        .map(([key]) => key)
    );
  }, [form?.background?.skills]);
  const lockedExpertise = useMemo(() => {
    return new Set([...raceExpertise, ...backgroundExpertise]);
  }, [raceExpertise, backgroundExpertise]);
  const lockedProficiencies = useMemo(() => {
    return new Set([...raceProficiencies, ...backgroundProficiencies, ...(primalChosen ? [primalChosen] : []), ...getBonusProficiencies(form)]);
  }, [raceProficiencies, backgroundProficiencies, primalChosen, form]);
  const currentProficiencyCount = Object.values(formSkills).filter(
    (s) => s.proficient
  ).length;
  const [proficiencyPointsLeft, setProficiencyPointsLeft] = useState(
    Math.max(0, formProficiencyPoints - currentProficiencyCount)
  );
  const currentExpertiseCount = Object.values(formSkills).filter(
    (s) => s.expertise
  ).length;
  const [expertisePointsLeft, setExpertisePointsLeft] = useState(
    Math.max(0, formExpertisePoints - currentExpertiseCount)
  );

  useEffect(() => {
    const count = Object.values(formSkills).filter(
      (s) => s.proficient
    ).length;
    setSkills(formSkills);
    setProficiencyPointsLeft(
      Math.max(0, formProficiencyPoints - count)
    );
    const expertiseUsed = Object.values(formSkills).filter(
      (s) => s.expertise
    ).length;
    setExpertisePointsLeft(
      Math.max(0, formExpertisePoints - expertiseUsed)
    );
  }, [
    formSkills,
    formProficiencyPoints,
    formExpertisePoints,
    lockedProficiencies,
    lockedExpertise,
  ]);

  const hasEquipment =
    typeof safeForm.equipment === 'object' && safeForm.equipment !== null;
  const normalizedEquipment = useMemo(
    () => normalizeEquipmentMap(safeForm.equipment),
    [safeForm.equipment]
  );
  const modMap = {
    str: strMod,
    dex: dexMod,
    con: conMod,
    int: intMod,
    wis: wisMod,
    cha: chaMod,
  };

  const equippedItems = useMemo(() => {
    if (hasEquipment) {
      return Object.values(normalizedEquipment).filter(Boolean);
    }
    return Array.isArray(safeForm.item) ? safeForm.item.filter(Boolean) : [];
  }, [safeForm.item, hasEquipment, normalizedEquipment]);

  const itemTotals = SKILLS.reduce((acc, { key }) => {
    acc[key] = equippedItems.reduce(
      (sum, el) => sum + Number(el.skillBonuses?.[key] || 0),
      0
    );
    return acc;
  }, {});

  const featTotals = SKILLS.reduce((acc, { key }) => {
    acc[key] = (safeForm.feat || []).reduce(
      (sum, el) => sum + Number(el[key] || 0),
      thaumaturgeSkillBonus(safeForm, key, wisMod) + magicianSkillBonus(safeForm, key, wisMod)
    );
    return acc;
  }, {});

  const raceTotals = SKILLS.reduce((acc, { key }) => {
    acc[key] = Number(safeForm.race?.[key] || 0);
    return acc;
  }, {});

  const profBonus = proficiencyBonus(totalLevel);

  const selectableSkills = new Set(safeForm.allowedSkills || []);
  const selectableExpertise = new Set(safeForm.allowedExpertise || []);

  const skillRecords = useMemo(() => SKILLS.map((skill) => {
    const { key, ability } = skill;
    const current = skills[key] || {};
    const isProficient = Boolean(current.proficient || lockedProficiencies.has(key));
    const expertise = Boolean(current.expertise || lockedExpertise.has(key));
    const proficiencyValue = profBonus * (expertise ? 2 : isProficient ? 1 : 0) + jackOfAllTradesBonus(safeForm, profBonus, isProficient);
    return {
      ...skill,
      proficient: isProficient,
      expertise,
      proficiencyValue,
      itemBonus: itemTotals[key],
      featBonus: featTotals[key],
      raceBonus: raceTotals[key],
      total: withBeastFormSkill(safeForm, key, modMap[ability] + proficiencyValue + itemTotals[key] + featTotals[key] + raceTotals[key]),
    };
  }), [safeForm, skills, lockedProficiencies, lockedExpertise, profBonus, itemTotals, featTotals, raceTotals, strMod, dexMod, conMod, intMod, wisMod, chaMod]);

  const visibleSkillGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = skillRecords.filter((skill) => {
      const matchesQuery = !query || [skill.label, ABILITY_LABELS[skill.ability], skill.description]
        .some((value) => value.toLowerCase().includes(query));
      const matchesFilter = skillFilter === 'all'
        || (skillFilter === 'proficient' && skill.proficient)
        || (skillFilter === 'expertise' && skill.expertise)
        || (skillFilter === 'untrained' && !skill.proficient)
        || skillFilter === skill.ability;
      return matchesQuery && matchesFilter;
    });
    const byAbility = ABILITY_ORDER.map((ability) => ({
      ability,
      skills: filtered.filter((skill) => skill.ability === ability).sort((a, b) => {
        if (sortOrder === 'highest') return b.total - a.total || a.label.localeCompare(b.label);
        if (sortOrder === 'alphabetical') return a.label.localeCompare(b.label);
        if (sortOrder === 'proficient') return Number(b.proficient) - Number(a.proficient) || b.total - a.total;
        if (sortOrder === 'expertise') return Number(b.expertise) - Number(a.expertise) || b.total - a.total;
        return 0;
      }),
    }));
    return sortOrder === 'highest' || sortOrder === 'alphabetical' || sortOrder === 'proficient' || sortOrder === 'expertise'
      ? byAbility.filter((group) => group.skills.length)
      : byAbility.filter((group) => group.skills.length);
  }, [skillRecords, searchQuery, skillFilter, sortOrder]);

  const strongestSkill = useMemo(
    () => skillRecords.reduce((best, skill) => (!best || skill.total > best.total ? skill : best), null),
    [skillRecords],
  );
  const passivePerception = (skillRecords.find((skill) => skill.key === 'perception')?.total || 0) + 10;
  const activeSkill = skillRecords.find((skill) => skill.key === selectedSkill) || null;

  const dialogClassName = useMemo(() => {
    if (!isDocked) {
      return undefined;
    }

    const classes = ['docked-modal'];
    if (dockedSide) {
      classes.push(`docked-modal--${dockedSide}`);
    }
    classes.push('docked-modal--skills');
    return classes.join(' ');
  }, [isDocked, dockedSide]);

  const modalClassName = useMemo(() => {
    const classes = ['dnd-modal', 'modern-modal'];
    if (isDocked) {
      classes.push('docked-modal-container');
    }
    return classes.join(' ');
  }, [isDocked]);

  const handleModalHide = useCallback(() => {
    if (isDocked) {
      if (typeof onDockClose === 'function') {
        onDockClose();
      }
      return;
    }

    handleCloseSkill?.();
  }, [isDocked, onDockClose, handleCloseSkill]);

  if (!form) {
    return <div>Loading...</div>;
  }

  async function updateSkill(skill, updated) {
    try {
      const res = await apiFetch(`/skills/update-skills/${params.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skill, ...updated }),
      });
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || 'Failed to update skill');
      }
      const data = await res.json();
      setSkills((prev) => {
        const newSkills = {
          ...prev,
          [skill]: { proficient: data.proficient, expertise: data.expertise },
        };
        const proficientCount = Object.values(newSkills).filter(
          (s) => s.proficient
        ).length;
        setProficiencyPointsLeft(
          Math.max(0, (safeForm.proficiencyPoints || 0) - proficientCount)
        );
        onSkillsChange?.(newSkills);
        return newSkills;
      });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error updating skill');
    }
  }

  const toggleProficient = (skill) => {
    if (lockedProficiencies.has(skill)) return;
    if (!selectableSkills.has(skill)) return;
    const current = skills[skill] || { proficient: false, expertise: false };
    if (!current.proficient && proficiencyPointsLeft <= 0) return;
    const updated = {
      proficient: !current.proficient,
      expertise: current.proficient ? current.expertise : false,
    };
    if (!updated.proficient) {
      updated.expertise = false;
    }
    updateSkill(skill, updated);
  };

  const toggleExpertise = (skill) => {
    const current = skills[skill] || { proficient: false, expertise: false };
    if (!(current.proficient || lockedProficiencies.has(skill))) return;
    if (!selectableExpertise.has(skill)) return;
    if (!current.expertise && expertisePointsLeft <= 0) return;
    const updated = {
      proficient: current.proficient,
      expertise: !current.expertise,
    };
    updateSkill(skill, updated);
  };

  const executeSkillRoll = async (skillKey, ability, proficient, expertise, labelSuffix = '', options = {}) => {
    const { closeParentBeforeRoll = true } = options;
    const skill = SKILLS.find((s) => s.key === skillKey);
    const skillLabel = skill?.label || skill?.name || skillKey;
    const proficiencyValue = profBonus * (expertise ? 2 : proficient ? 1 : 0) + jackOfAllTradesBonus(safeForm, profBonus, proficient);
    const bonus =
      modMap[ability] +
      proficiencyValue +
      itemTotals[skillKey] +
      featTotals[skillKey] +
      raceTotals[skillKey];
    const abilityLabel =
      ABILITY_LABELS[ability] || ability?.toUpperCase?.() || ability || 'Ability';

    if (closeParentBeforeRoll && !isDocked) {
      handleCloseSkill?.();
    }

    const rollModeResult = resolveAbilityCheckRollMode(safeForm, ability, { combatState, skillKey });
    const diceModifiers = getBuffSkillDiceModifiers(combatState, String(safeForm.characterId || safeForm._id), skillKey);
    const { result, d20, rolledD20s, keptD20, rollMode, reliableTalent, raisedD20, modifierDice = [], indomitableMight, heroicReroll } = await rollSkillWithDiceBox(bonus, {
        minimumD20: checkMinimumD20(combatState, String(safeForm.characterId || safeForm._id), ability),
      diceModifiers,
      diceColor: diceFaceColor,
      rollMode: rollModeResult.mode,
      rollContext: 'skill-check',
      proficient: Boolean(proficient || expertise),
      character: safeForm,
      source: skillLabel,
      ability,
      strengthScore: strScore,
    });
    const actualRollMode = rollMode || rollModeResult.mode;
    const breakdownParts = [actualRollMode === 'advantage' || actualRollMode === 'disadvantage'
      ? `${keptD20 ?? d20} (d20) (Rolled ${(rolledD20s || [d20]).join(' and ')})`
      : `${d20} (d20)`];

    const segments = [
      formatAdjustmentSegment(modMap[ability], `${abilityLabel} Modifier`),
      proficiencyValue
        ? formatAdjustmentSegment(
            proficiencyValue,
            expertise ? 'Expertise Bonus' : proficient ? 'Proficiency Bonus' : 'Jack of All Trades'
          )
        : null,
      formatAdjustmentSegment(itemTotals[skillKey], 'Item Bonus'),
      formatAdjustmentSegment(featTotals[skillKey], 'Feat Bonus'),
      formatAdjustmentSegment(raceTotals[skillKey], 'Race Bonus'),
    ];

    if (reliableTalent) breakdownParts.push('→ 10 (Reliable Talent)');
    if (raisedD20) breakdownParts.push('→ 15 (Glibness)');
    const spellBreakdown = modifierDiceBreakdown(diceModifiers, modifierDice);
    if (spellBreakdown) breakdownParts.push(spellBreakdown);
    segments.filter(Boolean).forEach((segment) => {
      breakdownParts.push(segment);
    });
    if (indomitableMight) breakdownParts.push(indomitableMightNote({ result, indomitableMight }));

    const diceRolls = [
      {
        sides: 20,
        value: d20,
        type: `${skillLabel} Check`,
        category: 'base',
      },
      ...modifierDiceRecords(diceModifiers, modifierDice),
    ];
    window.dispatchEvent(
      new CustomEvent('damage-roll', {
        detail: {
          value: result,
          breakdown: breakdownParts.join(' '),
          source: labelSuffix ? `${abilityLabel} (${skillLabel}) ${labelSuffix}` : skillLabel,
          rollLabel: 'Skill Roll',
          heroicReroll,
          effectiveD20: raisedD20 ? 15 : reliableTalent ? 10 : (keptD20 ?? d20),
          critical: d20 === 20,
          fumble: d20 === 1,
          diceRolls,
          rollMode: actualRollMode,
          advantageSources: rollModeResult.advantageSources,
          disadvantageSources: rollModeResult.disadvantageSources,
        },
      })
    );
  };

  const rollTool = async () => {
    if (isRollingSkill || !proficientTools.includes(selectedTool)) return;
    setIsRollingSkill(true);
    if (!isDocked) handleCloseSkill?.();
    try {
      const mode = resolveAbilityCheckRollMode(safeForm, toolAbility, { combatState });
      const bonus = modMap[toolAbility] + profBonus;
      const rolled = await rollSkillWithDiceBox(bonus, { character: safeForm, proficient: true,
        rollContext: 'tool-check', rollMode: mode.mode, diceColor: diceFaceColor, source: selectedTool, ability: toolAbility, strengthScore: strScore });
      window.dispatchEvent(new CustomEvent('damage-roll', { detail: {
        value: rolled.result, source: selectedTool, rollLabel: 'Tool Check', effectiveD20: rolled.effectiveD20 ?? rolled.d20, heroicReroll: rolled.heroicReroll,
        breakdown: `${rolled.d20} (d20)${rolled.reliableTalent ? ' → 10 (Reliable Talent)' : ''} + ${bonus} (${ABILITY_LABELS[toolAbility]} + Proficiency) = ${rolled.result}`,
        diceRolls: [{ sides: 20, value: rolled.d20, rolls: rolled.rolledD20s, type: 'Tool Check', category: 'base' }],
        rollMode: rolled.rollMode, advantageSources: mode.advantageSources, disadvantageSources: mode.disadvantageSources,
      } }));
    } finally { setIsRollingSkill(false); }
  };

  const handleRoll = async (skillKey, ability, proficient, expertise) => {
    if (isRollingSkill || modifierPrompt) return;
    if (canUsePrimalKnowledgeForSkill(safeForm, skillKey, combatState)) {
      setModifierPrompt({ skillKey, ability, proficient, expertise });
      setSelectedModifierAbility(ability);
      return;
    }
    setIsRollingSkill(true);
    try {
      await executeSkillRoll(skillKey, ability, proficient, expertise);
    } finally {
      setIsRollingSkill(false);
    }
  };

  const confirmModifierPrompt = async () => {
    if (!modifierPrompt || isRollingSkill) return;
    const prompt = { ...modifierPrompt };
    const ability = selectedModifierAbility || prompt.ability;
    const suffix = ability === 'str' && ability !== prompt.ability ? '— Primal Knowledge' : '';
    if (suffix && !canUsePrimalKnowledgeForSkill(safeForm,prompt.skillKey,combatState)) {closeModifierPrompt();setError('Rage is no longer active. Roll the skill again using its normal ability.');return;}

    closeModifierPrompt();
    if (!isDocked) {
      handleCloseSkill?.();
    }

    setIsRollingSkill(true);
    try {
      await executeSkillRoll(
        prompt.skillKey,
        ability,
        prompt.proficient,
        prompt.expertise,
        suffix,
        { closeParentBeforeRoll: false }
      );
    } finally {
      setIsRollingSkill(false);
    }
  };

  const closeModifierPrompt = () => {
    if (isRollingSkill) return;
    setModifierPrompt(null);
    setSelectedModifierAbility('');
  };

  const handleView = (skill) => {
    setSelectedSkill(skill);
  };


  return (
    <>
      <Modal
        className={modalClassName}
        show={showSkill}
        onHide={handleModalHide}
        size="lg"
        scrollable
        centered={!isDocked}
        backdrop={isDocked ? false : true}
        enforceFocus={!isDocked}
        restoreFocus={!isDocked}
        dialogClassName={dialogClassName}
      >
        <Card className="modern-card text-center skill-codex-card-shell">
          <Card.Header className="modal-header">
            <DockControls
              dockedSide={dockedSide}
              onDockChange={onDockChange}
              isDocked={isDocked}
            />
            <Card.Title className="modal-title">Skills</Card.Title>
          </Card.Header>
          <Card.Body className="skill-codex-body">
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            )}
            {primalProfile.eligible && <section className="mb-3 text-start" aria-label="Primal Knowledge">
              <strong>Primal Knowledge</strong>
              {primalChosen ? <p>Chosen proficiency: {SKILLS.find(s=>s.key===primalChosen)?.label || primalChosen}</p> : <>
                <p>Choose one additional Barbarian skill proficiency.</p>
                <Form.Label htmlFor="primal-knowledge-skill">Primal Knowledge skill</Form.Label>
                <Form.Select id="primal-knowledge-skill" value={primalSkill} disabled={savingPrimal} onChange={event=>setPrimalSkill(event.target.value)}>
                  <option value="">Choose a skill</option>
                  {primalProfile.available.map(key=><option key={key} value={key}>{SKILLS.find(s=>s.key===key)?.label || key}</option>)}
                </Form.Select>
                <Button className="mt-2" disabled={savingPrimal || !primalSkill} onClick={choosePrimal}>{savingPrimal?'Saving…':'Save Primal Knowledge'}</Button>
                {primalProfile.available.length===0 && <p>You already have proficiency in every eligible skill.</p>}
              </>}
            </section>}
            {proficientTools.length > 0 && <details className="mb-3 text-start">
              <summary>Tool checks</summary>
              <Form.Label htmlFor="proficient-tool">Tool</Form.Label>
              <Form.Select id="proficient-tool" value={selectedTool} onChange={event => setSelectedTool(event.target.value)}>
                <option value="">Choose a proficient tool</option>
                {proficientTools.map(tool => <option key={tool} value={tool}>{tool}</option>)}
              </Form.Select>
              <Form.Label htmlFor="tool-check-ability">Tool ability</Form.Label>
              <Form.Select id="tool-check-ability" value={toolAbility} onChange={event => setToolAbility(event.target.value)}>
                {ABILITY_ORDER.map(ability => <option key={ability} value={ability}>{ABILITY_LABELS[ability]}</option>)}
              </Form.Select>
              <Button className="mt-2" onClick={rollTool} disabled={isRollingSkill || !proficientTools.includes(selectedTool)}>Roll tool check</Button>
            </details>}
            <section className="skill-codex-summary" aria-label="Skill summary">
              <div className="skill-codex-summary__title">
                <span className="skill-codex-kicker">Adventurer's reference</span>
                <h2>Skill Codex</h2>
                <p>Master your strengths, chart your training, and roll with confidence.</p>
              </div>
              <div className="skill-codex-stats">
                <div className={`skill-codex-stat ${proficiencyPointsLeft === 0 ? 'is-complete' : ''}`}>
                  <span>Proficiencies remaining</span><strong>{proficiencyPointsLeft === 0 ? '✓ Complete' : proficiencyPointsLeft}</strong>
                </div>
                <div className={`skill-codex-stat ${expertisePointsLeft === 0 ? 'is-complete' : ''}`}>
                  <span>Expertise remaining</span><strong>{expertisePointsLeft === 0 ? '✓ Complete' : expertisePointsLeft}</strong>
                </div>
                <div className="skill-codex-stat"><span>Highest skill</span><strong>{strongestSkill ? `${strongestSkill.label} ${formatBonus(strongestSkill.total)}` : '—'}</strong></div>
                <div className="skill-codex-stat"><span>Passive perception</span><strong>{passivePerception}</strong></div>
                <div className="skill-codex-stat"><span>Proficiency bonus</span><strong>{formatBonus(profBonus)}</strong></div>
              </div>
            </section>

            <section className="skill-codex-toolbar" aria-label="Search and filter skills">
              <label className="skill-codex-search">
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                <span className="visually-hidden">Search skills</span>
                <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search skills, abilities, or lore" />
              </label>
              <div className="skill-codex-filter-row" role="group" aria-label="Skill status filters">
                {[
                  ['all', 'All'], ['proficient', 'Proficient'], ['expertise', 'Expertise'], ['untrained', 'Untrained'],
                ].map(([value, label]) => <button key={value} type="button" className={`skill-filter-chip ${skillFilter === value ? 'is-active' : ''}`} onClick={() => setSkillFilter(value)} aria-pressed={skillFilter === value}>{label}</button>)}
              </div>
              <label className="skill-codex-sort">Sort
                <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} aria-label="Sort skills">
                  <option value="ability">Ability</option><option value="highest">Highest bonus</option><option value="alphabetical">Alphabetical</option><option value="proficient">Proficient</option><option value="expertise">Expertise</option>
                </select>
              </label>
            </section>

            <div className="skill-codex-layout">
              <main className="skill-codex-library" aria-live="polite">
                {visibleSkillGroups.length ? visibleSkillGroups.map(({ ability, skills: abilitySkills }) => (
                  <section key={ability} className={`skill-ability-group skill-ability-group--${ability}`}>
                    <button type="button" className="skill-ability-heading" onClick={() => setCollapsedAbilities((previous) => ({ ...previous, [ability]: !previous[ability] }))} aria-expanded={!collapsedAbilities[ability]}>
                      <span className="skill-ability-icon" aria-hidden="true">{ABILITY_ICONS[ability]}</span>
                      <span><small>{ABILITY_LABELS[ability]}</small><strong>{formatBonus(modMap[ability])} modifier</strong></span>
                      <span className="skill-ability-count">{abilitySkills.length} skills <i className={`fa-solid fa-chevron-${collapsedAbilities[ability] ? 'down' : 'up'}`} aria-hidden="true" /></span>
                    </button>
                    {!collapsedAbilities[ability] && <div className="skill-codex-grid">
                      {abilitySkills.map((skill) => {
                        const isSelectable = selectableSkills.has(skill.key);
                        const canToggleProficiency = isSelectable && !lockedProficiencies.has(skill.key) && (skill.proficient || proficiencyPointsLeft > 0);
                        const canToggleExpertise = skill.proficient && selectableExpertise.has(skill.key) && !lockedExpertise.has(skill.key) && (skill.expertise || expertisePointsLeft > 0);
                        const status = skill.expertise ? 'Expertise' : skill.proficient ? 'Proficient' : 'Not trained';
                        return <article key={skill.key} className={`skill-codex-card ${selectedSkill === skill.key ? 'is-selected' : ''}`}>
                          <button type="button" className="skill-codex-card__main" onClick={() => handleView(skill.key)} aria-label={`View ${skill.label} details`}>
                            <span className="skill-codex-card__bonus">{formatBonus(skill.total)}</span>
                            <span className="skill-codex-card__identity"><strong>{skill.label}</strong><small>{ABILITY_LABELS[skill.ability]} · {skill.ability.toUpperCase()} {formatBonus(modMap[skill.ability])}</small></span>
                          </button>
                          <div className="skill-codex-card__actions">
                            <span className={`skill-status-chip is-${status.toLowerCase().replace(' ', '-')}`}>{status}</span>
                            <button type="button" className="skill-icon-button" onClick={() => handleRoll(skill.key, skill.ability, skill.proficient, skill.expertise)} aria-label={`roll ${skill.label}`} title="Roll skill check"><i className="fa-solid fa-dice-d20" aria-hidden="true" /></button>
                          </div>
                          <div className="skill-codex-card__training" role="group" aria-label={`${skill.label} training`}>
                            <button type="button" className={`skill-training-chip ${skill.proficient ? 'is-active' : ''}`} disabled={!canToggleProficiency} onClick={() => toggleProficient(skill.key)} aria-pressed={skill.proficient}>Proficient</button>
                            <button type="button" className={`skill-training-chip skill-training-chip--expertise ${skill.expertise ? 'is-active' : ''}`} disabled={!canToggleExpertise} onClick={() => toggleExpertise(skill.key)} aria-pressed={skill.expertise}>Expertise</button>
                          </div>
                        </article>;
                      })}
                    </div>}
                  </section>
                )) : <div className="skill-codex-empty">No skills match this search. Clear a filter to reveal your full codex.</div>}
              </main>
              <aside
                className={`skill-codex-inspector ${activeSkill ? 'is-open' : ''}`}
                aria-label="Skill details"
                aria-modal={activeSkill ? 'true' : undefined}
                role={activeSkill ? 'dialog' : undefined}
              >
                {activeSkill ? <>
                  <div className="skill-codex-inspector__heading"><span className={`skill-status-chip is-${(activeSkill.expertise ? 'expertise' : activeSkill.proficient ? 'proficient' : 'not-trained')}`}>{activeSkill.expertise ? 'Expertise' : activeSkill.proficient ? 'Proficient' : 'Not trained'}</span><button type="button" className="skill-icon-button" onClick={() => setSelectedSkill(null)} aria-label="Close skill details"><i className="fa-solid fa-xmark" /></button></div>
                  <span className="skill-codex-inspector__ability">{ABILITY_ICONS[activeSkill.ability]} {ABILITY_LABELS[activeSkill.ability]}</span><h3>{activeSkill.label} <strong>{formatBonus(activeSkill.total)}</strong></h3><p>{activeSkill.description}</p>
                  <h4>Bonus breakdown</h4>
                  <dl className="skill-breakdown"><div><dt>Ability modifier</dt><dd>{formatBonus(modMap[activeSkill.ability])}</dd></div><div><dt>{activeSkill.expertise ? 'Expertise bonus' : activeSkill.proficient || !activeSkill.proficiencyValue ? 'Proficiency bonus' : 'Jack of All Trades'}</dt><dd>{formatBonus(activeSkill.proficiencyValue)}</dd></div><div><dt>Equipment & features</dt><dd>{formatBonus(activeSkill.itemBonus + activeSkill.featBonus + activeSkill.raceBonus)}</dd></div></dl>
                  {resolveAbilityCheckRollMode(safeForm, activeSkill.ability, { skillKey: activeSkill.key }).disadvantageSources.includes('Armor (Stealth)') && <p>Armor imposes disadvantage on Dexterity (Stealth) checks.</p>}
                  <Button className="skill-inspector-roll" onClick={() => handleRoll(activeSkill.key, activeSkill.ability, activeSkill.proficient, activeSkill.expertise)} aria-label={`roll ${activeSkill.label}`}>Roll {activeSkill.label}</Button>
                </> : <div className="skill-codex-inspector__empty"><i className="fa-solid fa-book-open" aria-hidden="true" /><strong>Skill inspector</strong><p>Select a skill to view its lore, current bonus, and training details.</p></div>}
              </aside>
            </div>
          </Card.Body>
          <Card.Footer className="modal-footer d-flex skill-codex-footer">
            <Button
              onClick={() => handleCloseSkill()}
              className="action-btn close-btn skill-codex-close-button"
            >Close</Button>
          </Card.Footer>
        </Card>
      </Modal>
      <Modal
        className="dnd-modal modern-modal primal-modifier-modal"
        show={Boolean(modifierPrompt)}
        onHide={closeModifierPrompt}
        centered
        restoreFocus
        enforceFocus
      >
        <Card className="modern-card text-center">
          <Card.Header className="modal-header">
            <Card.Title className="modal-title">Choose Modifier</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-0" controlId="primal-knowledge-modifier">
              <Form.Label className="text-light">Modifier:</Form.Label>
              <Form.Select
                value={selectedModifierAbility}
                onChange={(event) => setSelectedModifierAbility(event.target.value)}
                autoFocus
              >
                {modifierPrompt && [modifierPrompt.ability, 'str']
                  .filter((value, index, array) => array.indexOf(value) === index)
                  .map((abilityKey) => (
                    <option key={abilityKey} value={abilityKey}>
                      {ABILITY_LABELS[abilityKey] || abilityKey.toUpperCase()} ({modMap[abilityKey] >= 0 ? '+' : ''}{modMap[abilityKey]}){abilityKey === 'str' && abilityKey !== modifierPrompt.ability ? ' — Primal Knowledge' : ''}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Card.Body>
          <Card.Footer className="modal-footer">
            <Button className="action-btn close-btn" onClick={closeModifierPrompt} disabled={isRollingSkill}>
              Cancel
            </Button>
            <Button className="action-btn save-btn" onClick={confirmModifierPrompt} disabled={isRollingSkill}>
              Roll
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </>
  );
}
